import { usePostHog } from "posthog-js/react"
import { useState } from "react"

import { Filters } from "@costory/types/filters"

import { DataTableExplorer } from "@costory/front/components/charts/DataTable"
import { TableChart } from "@costory/front/components/charts/TableExplorer"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  useExplorerChangeDataPointQuery,
  useWaterfallQuery,
} from "@costory/front/queries/explore"
import { getBestDrillDown } from "@costory/front/utils/columns"

type Props = {
  filters: Filters
  drillDownInto: (
    groupBy: string,
    value: string,
    newGroupBy: string,
    mode: "sameWindow" | "newTab",
  ) => void
}

export const ExplorerTable = ({ filters, drillDownInto }: Props) => {
  const waterfallQuery = useWaterfallQuery(filters)
  const explorerQuery = useExplorerChangeDataPointQuery(filters, false)
  const [isDataTable, setIsDataTable] = useState(false)
  const posthog = usePostHog()

  // Helper to wrap drillDownInto with PostHog event and context
  const handleDrillDown = (value: string, mode: "sameWindow" | "newTab") => {
    const eventName = isDataTable
      ? "Explorer Table View Changed"
      : "Waterfall Table Drill Down"
    posthog.capture(eventName, {
      dimensionValue: value,
      mode,
      source: isDataTable ? "DataTableExplorer" : "WaterfallTableChart",
      groupBy: filters.groupBy,
      context: isDataTable ? "explorer" : "waterfall",
    })
    drillDownInto(
      filters.groupBy,
      value,
      getBestDrillDown(filters.groupBy),
      mode,
    )
  }

  return (
    <>
      {isDataTable ? (
        <QueryWrapper query={explorerQuery}>
          {({ data }) => (
            <DataTableExplorer
              data={data.dataTable}
              drillDownInto={handleDrillDown}
              isDataTable={isDataTable}
              setIsDataTable={setIsDataTable}
            />
          )}
        </QueryWrapper>
      ) : (
        <QueryWrapper query={waterfallQuery}>
          {({ data }) => (
            <TableChart
              data={data.dataGroupedByValue}
              currency={filters.currency}
              drillDownInto={handleDrillDown}
              isDataTable={isDataTable}
              setIsDataTable={setIsDataTable}
            />
          )}
        </QueryWrapper>
      )}
    </>
  )
}
